import {
  useGetListOfStatusQuery,
  useGetProjectByIdQuery,
  useUpdateProjectMutation,
  useUpdateStatusMutation,
} from "@/features/bughurd/api";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Pagination,
  Chip,
  InputAdornment,
  Alert,
  Switch,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useState, useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import { ChromePicker } from "react-color";
import { Popover } from "@mui/material";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckIcon from "@mui/icons-material/Check";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import IconifyIcon from "@mui/icons-material/Extension";
import PaletteIcon from "@mui/icons-material/Palette";
import DescriptionIcon from "@mui/icons-material/Description";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Icon } from "@iconify/react";
import CustomLoader from "@/Components/Common/CustomLoader";
import DEFEKT_TRACKER from "@/app/permissionNew/defectTracker";
import { useAppSelector } from "@/app/hooks";

// Popular icon collections for filtering
const ICON_COLLECTIONS = [
  "material-symbols",
  "mdi",
  "heroicons",
  "lucide",
  "tabler",
  "carbon",
  "ph",
  "fluent",
  "ant-design",
  "bootstrap",
  "bi",
  "fa6-solid",
  "fa6-regular",
  "fa6-brands",
  "solar",
  "mingcute",
  "iconamoon",
  "hugeicons",
  "lets-icons",
  "streamline",
];

// Popular status-related icons for initial display
const POPULAR_STATUS_ICONS = [
  { name: "material-symbols:check-circle", label: "Check Circle" },
  { name: "material-symbols:play-circle", label: "Play Circle" },
  { name: "material-symbols:pause-circle", label: "Pause Circle" },
  { name: "material-symbols:cancel", label: "Cancel" },
  { name: "material-symbols:pending", label: "Pending" },
  { name: "mdi:bug", label: "Bug" },
  { name: "mdi:progress-check", label: "Progress Check" },
  { name: "heroicons:flag", label: "Flag" },
  { name: "lucide:star", label: "Star" },
  { name: "tabler:hourglass", label: "Hourglass" },
  { name: "carbon:task", label: "Task" },
  { name: "ph:warning-circle", label: "Warning" },
  { name: "fluent:status-24-filled", label: "Status" },
  { name: "ant-design:check-circle-filled", label: "Done" },
  { name: "bi:exclamation-circle-fill", label: "Alert" },
  { name: "mdi:clock-outline", label: "Clock" },
  { name: "heroicons:x-circle", label: "Error" },
  { name: "lucide:activity", label: "Activity" },
  { name: "tabler:circle-check", label: "Circle Check" },
  { name: "carbon:incomplete", label: "Incomplete" },
  { name: "ph:circle-dashed", label: "Dashed Circle" },
  { name: "fluent:circle-hint-24-filled", label: "Hint" },
  { name: "ant-design:loading-3-quarters-outlined", label: "Loading" },
  { name: "bi:arrow-right-circle", label: "Next" },
  { name: "mdi:timer-sand", label: "Timer" },
  { name: "material-symbols:inbox", label: "Inbox" },
  { name: "mdi:rocket-launch", label: "Launch" },
  { name: "heroicons:fire", label: "Fire" },
  { name: "lucide:zap", label: "Zap" },
  { name: "tabler:target", label: "Target" },
  { name: "carbon:development", label: "Development" },
  { name: "ph:gear", label: "Gear" },
  { name: "fluent:sparkle-24-filled", label: "Sparkle" },
  { name: "ant-design:thunderbolt-filled", label: "Thunderbolt" },
  { name: "bi:shield-check", label: "Shield Check" },
  { name: "mdi:account-check", label: "Account Check" },
  { name: "heroicons:academic-cap", label: "Academic Cap" },
  { name: "lucide:trending-up", label: "Trending Up" },
  { name: "tabler:calendar-check", label: "Calendar Check" },
  { name: "carbon:notification", label: "Notification" },
];

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`appearance-tabpanel-${index}`}
      aria-labelledby={`appearance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AppearanceTab({
  isMyTaskView = false,
  projectDetails,
}) {
  const { roleCodeDetails: myPermissions } = useAppSelector(
    (state) => state.appGlobal
  );
  const { id } = useParams();
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const topOffset = containerRef.current.getBoundingClientRect().top;
        const viewportHeight = window.innerHeight;
        const availableHeight = viewportHeight - topOffset - 20;
        setContainerHeight(availableHeight);
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  const {
    data: statusList,
    isLoading,
    isFetching,
  } = useGetListOfStatusQuery(
    { projectId: isMyTaskView ? "" : id },
    {
      // skip: !id,
      refetchOnMountOrArgChange: true,
    }
  );

  // Card expiration colors
  const [expiredCardColor, setExpiredCardColor] = useState("#ffebee");
  const [nearExpiryCardColor, setNearExpiryCardColor] = useState("#fff8e1");
  const [anchorElExpired, setAnchorElExpired] = useState(null);
  const [anchorElNearExpiry, setAnchorElNearExpiry] = useState(null);

  // Column colors
  const [updateStatus] = useUpdateStatusMutation();
  const [statusColors, setStatusColors] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStatusId, setSelectedStatusId] = useState(null);
  const [updateProject, { isLoading: loadingProject }] =
    useUpdateProjectMutation();

  // Status icons
  const [statusIcons, setStatusIcons] = useState({});
  const [iconDialogOpen, setIconDialogOpen] = useState(false);
  const [selectedStatusForIcon, setSelectedStatusForIcon] = useState(null);
  const [searchIconTerm, setSearchIconTerm] = useState("");
  const [selectedIconSet, setSelectedIconSet] = useState("");
  const [customIconName, setCustomIconName] = useState("");
  const [iconPage, setIconPage] = useState(1);
  const [isLoadingIcons, setIsLoadingIcons] = useState(false);
  const [availableIcons, setAvailableIcons] = useState([]);
  const [totalIconsCount, setTotalIconsCount] = useState(0);
  const [showingPopularIcons, setShowingPopularIcons] = useState(true);
  const iconsPerPage = 60;

  // Status descriptions and hide toggles
  const [statusDescriptions, setStatusDescriptions] = useState({});
  const [statusHideToggles, setStatusHideToggles] = useState({});

  // Temporary state for edit mode - these are the working copies during editing
  const [tempStatusColors, setTempStatusColors] = useState({});
  const [tempStatusIcons, setTempStatusIcons] = useState({});
  const [tempStatusDescriptions, setTempStatusDescriptions] = useState({});
  const [tempStatusHideToggles, setTempStatusHideToggles] = useState({});

  // Load saved appearance settings
  useEffect(() => {
    if (projectDetails?.bugColor) {
      const appearance = projectDetails?.bugColor;
      if (appearance.expiredCardColor) {
        setExpiredCardColor(appearance.expiredCardColor);
      }
      if (appearance.nearExpiryCardColor) {
        setNearExpiryCardColor(appearance.nearExpiryCardColor);
      }
    }
  }, [projectDetails]);

  // Load status data from statusList and initialize both current and temp states
  useEffect(() => {
    if (statusList?.data?.decryptedData?.items) {
      const iconsMap = {};
      const descriptionsMap = {};
      const hideTogglesMap = {};
      const colorsMap = {};

      statusList.data.decryptedData.items.forEach((status) => {
        if (status.icon) {
          iconsMap[status._id] = status.icon;
        }
        if (status.description !== undefined) {
          descriptionsMap[status._id] = status.description || "";
        }
        if (status.hidden !== undefined) {
          hideTogglesMap[status._id] = status.hidden;
        }
        if (status.color) {
          colorsMap[status._id] = status.color;
        }
      });

      // Set current states
      setStatusIcons(iconsMap);
      setStatusDescriptions(descriptionsMap);
      setStatusHideToggles(hideTogglesMap);
      setStatusColors(colorsMap);

      // Initialize temp states with current values
      setTempStatusColors(colorsMap);
      setTempStatusIcons(iconsMap);
      setTempStatusDescriptions(descriptionsMap);
      setTempStatusHideToggles(hideTogglesMap);
    }
  }, [statusList]);

  // Fetch icons from Iconify API
  const fetchIcons = useCallback(
    async (collection = "", search = "", page = 1) => {
      setIsLoadingIcons(true);
      setShowingPopularIcons(false);
      try {
        let url = "https://api.iconify.design/search";
        const params = new URLSearchParams();

        if (search) {
          params.append("query", search);
        }

        if (collection) {
          params.append("prefix", collection);
        }

        params.append("limit", iconsPerPage.toString());
        params.append("start", ((page - 1) * iconsPerPage).toString());

        const response = await fetch(`${url}?${params.toString()}`);
        const data = await response.json();

        if (data.icons) {
          const icons = data.icons.map((iconName) => ({
            name: iconName,
            label: iconName.split(":")[1]?.replace(/-/g, " ") || iconName,
          }));

          setAvailableIcons(icons);
          setTotalIconsCount(data.total || icons.length);
        } else {
          setAvailableIcons([]);
          setTotalIconsCount(0);
        }
      } catch (error) {
        console.error("Error fetching icons:", error);
        setAvailableIcons([]);
        setTotalIconsCount(0);
      } finally {
        setIsLoadingIcons(false);
      }
    },
    [iconsPerPage]
  );

  // Show popular icons initially
  const showPopularIcons = useCallback(() => {
    setShowingPopularIcons(true);
    setAvailableIcons(POPULAR_STATUS_ICONS);
    setTotalIconsCount(POPULAR_STATUS_ICONS.length);
    setIsLoadingIcons(false);
  }, []);

  const debouncedSearch = useCallback(
    debounce((collection, search, page) => {
      if (search.trim() || collection) {
        fetchIcons(collection, search, page);
      } else {
        showPopularIcons();
      }
    }, 500),
    [fetchIcons, showPopularIcons]
  );

  useEffect(() => {
    if (iconDialogOpen) {
      if (!searchIconTerm.trim() && !selectedIconSet) {
        showPopularIcons();
      } else {
        debouncedSearch(selectedIconSet, searchIconTerm, iconPage);
      }
    }
  }, [
    selectedIconSet,
    searchIconTerm,
    iconPage,
    iconDialogOpen,
    debouncedSearch,
    showPopularIcons,
  ]);

  const handleSaveAppearance = async () => {
    try {
      const payload = {
        projectId: id,
        name: projectDetails?.name,
        bugColor: {
          expiredCardColor,
          nearExpiryCardColor,
        },
        platform: projectDetails?.platform,
        owner: projectDetails?.owner,
        jiraConfig: [],
      };

      await updateProject(payload).unwrap();
      toast.success(t("Appearance settings updated successfully"));
    } catch (error) {
      toast.error(
        error?.data?.message || t("Failed to update appearance settings")
      );
    }
  };

  const handleSaveAllChanges = async () => {
    setIsSaving(true);
    try {
      const statusesToUpdate = statusList?.data?.decryptedData?.items || [];
      const updatePromises = [];

      for (const status of statusesToUpdate) {
        const statusId = status._id;
        const updateData = {};

        // Check what has changed and prepare update data
        if (tempStatusColors[statusId] !== statusColors[statusId]) {
          updateData.color = tempStatusColors[statusId];
        }

        if (tempStatusIcons[statusId] !== statusIcons[statusId]) {
          updateData.icon = tempStatusIcons[statusId];
        }

        if (tempStatusDescriptions[statusId] !== statusDescriptions[statusId]) {
          updateData.description = tempStatusDescriptions[statusId];
        }

        if (tempStatusHideToggles[statusId] !== statusHideToggles[statusId]) {
          updateData.hidden = tempStatusHideToggles[statusId];
        }

        // Only update if there are changes
        if (Object.keys(updateData).length > 0) {
          const payload = { statusId, ...updateData };
          updatePromises.push(updateStatus(payload).unwrap());
        }
      }

      // Wait for all updates to complete
      await Promise.all(updatePromises);

      // Update local states with temp values
      setStatusColors({ ...tempStatusColors });
      setStatusIcons({ ...tempStatusIcons });
      setStatusDescriptions({ ...tempStatusDescriptions });
      setStatusHideToggles({ ...tempStatusHideToggles });

      setIsEditMode(false);
      toast.success(t("All changes saved successfully"));
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error(error?.data?.message || t("Failed to save changes"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelChanges = () => {
    // Reset temp states to original values
    setTempStatusColors({ ...statusColors });
    setTempStatusIcons({ ...statusIcons });
    setTempStatusDescriptions({ ...statusDescriptions });
    setTempStatusHideToggles({ ...statusHideToggles });
    setIsEditMode(false);
  };

  const handleEditModeToggle = () => {
    if (isEditMode) {
      handleCancelChanges();
    } else {
      setIsEditMode(true);
    }
  };

  // Handle color change - only update temp state, no API call
  const handleColorChange = (statusId, color) => {
    if (!isEditMode) return; // Prevent changes if not in edit mode

    const colorValue = color.rgb
      ? `rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`
      : color.hex;

    setTempStatusColors((prev) => ({
      ...prev,
      [statusId]: colorValue,
    }));
  };

  // Handle description change - only update temp state, no API call
  const handleDescriptionChange = (statusId, description) => {
    if (!isEditMode) return; // Prevent changes if not in edit mode

    setTempStatusDescriptions((prev) => ({
      ...prev,
      [statusId]: description,
    }));
  };

  // Handle hide toggle change - only update temp state, no API call
  const handleHideToggleChange = (statusId, hidden) => {
    if (!isEditMode) return; // Prevent changes if not in edit mode

    setTempStatusHideToggles((prev) => ({
      ...prev,
      [statusId]: hidden,
    }));
  };

  const handleClick = (event, statusId) => {
    if (!isEditMode) return; // Prevent opening color picker if not in edit mode
    setAnchorEl(event.currentTarget);
    setSelectedStatusId(statusId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedStatusId(null);
  };

  const handleIconClick = (statusId) => {
    if (!isEditMode) return; // Prevent opening icon dialog if not in edit mode
    setSelectedStatusForIcon(statusId);
    setIconDialogOpen(true);
    setSearchIconTerm("");
    setCustomIconName("");
    setIconPage(1);
    setSelectedIconSet("");
    showPopularIcons();
  };

  const handleIconSelect = (iconName) => {
    if (selectedStatusForIcon && isEditMode) {
      setTempStatusIcons((prev) => ({
        ...prev,
        [selectedStatusForIcon]: iconName,
      }));
      setIconDialogOpen(false);
      setSelectedStatusForIcon(null);
    }
  };

  const handleRemoveIcon = (statusId) => {
    if (!isEditMode) return; // Prevent removing icon if not in edit mode
    setTempStatusIcons((prev) => ({
      ...prev,
      [statusId]: null,
    }));
  };

  const handleCustomIconAdd = () => {
    if (customIconName.trim() && selectedStatusForIcon && isEditMode) {
      if (customIconName.includes(":")) {
        setTempStatusIcons((prev) => ({
          ...prev,
          [selectedStatusForIcon]: customIconName.trim(),
        }));
        setIconDialogOpen(false);
        setSelectedStatusForIcon(null);
        setCustomIconName("");
      } else {
        toast.error(
          t(
            "Please enter a valid icon name (e.g., mdi:home, material-symbols:check)"
          )
        );
      }
    }
  };

  const handleExpiredColorClick = (event) => {
    setAnchorElExpired(event.currentTarget);
  };

  const handleExpiredColorClose = () => {
    setAnchorElExpired(null);
  };

  const handleNearExpiryColorClick = (event) => {
    setAnchorElNearExpiry(event.currentTarget);
  };

  const handleNearExpiryColorClose = () => {
    setAnchorElNearExpiry(null);
  };

  const handleCollectionFilter = (collection) => {
    setSelectedIconSet(collection);
    setIconPage(1);
    if (collection || searchIconTerm.trim()) {
      fetchIcons(collection, searchIconTerm, 1);
    } else {
      showPopularIcons();
    }
  };

  const handleSearchChange = (value) => {
    setSearchIconTerm(value);
    setIconPage(1);
    if (value.trim() || selectedIconSet) {
      debouncedSearch(selectedIconSet, value, 1);
    } else {
      showPopularIcons();
    }
  };

  const handlePageChange = (_, page) => {
    setIconPage(page);
    if (showingPopularIcons) {
      const startIndex = (page - 1) * iconsPerPage;
      const endIndex = startIndex + iconsPerPage;
      setAvailableIcons(POPULAR_STATUS_ICONS.slice(startIndex, endIndex));
    } else {
      fetchIcons(selectedIconSet, searchIconTerm, page);
    }
  };

  const totalPages = showingPopularIcons
    ? Math.ceil(POPULAR_STATUS_ICONS.length / iconsPerPage)
    : Math.ceil(totalIconsCount / iconsPerPage);

  useEffect(() => {
    if (showingPopularIcons) {
      const startIndex = (iconPage - 1) * iconsPerPage;
      const endIndex = startIndex + iconsPerPage;
      setAvailableIcons(POPULAR_STATUS_ICONS.slice(startIndex, endIndex));
      setTotalIconsCount(POPULAR_STATUS_ICONS.length);
    }
  }, [iconPage, showingPopularIcons, iconsPerPage]);

  const open = Boolean(anchorEl);
  const openExpired = Boolean(anchorElExpired);
  const openNearExpiry = Boolean(anchorElNearExpiry);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Combined Appearance Settings Tab Content
  const combinedAppearanceTab = (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        height: "fit-content",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",

          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Icon icon="material-symbols:settings" width="20" height="20" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t("Configure Appearance Settings")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {isEditMode ? (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveAllChanges}
                disabled={isSaving || !DEFEKT_TRACKER.UPDATE(myPermissions)}
              >
                {isSaving ? t("Saving...") : t("Save")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancelChanges}
                disabled={isSaving}
              >
                {t("Cancel")}
              </Button>
            </>
          ) : (
            !DEFEKT_TRACKER.HIDE_UPDATE(myPermissions) && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleEditModeToggle}
                disabled={!DEFEKT_TRACKER.UPDATE(myPermissions)}
              >
                {t("Edit")}
              </Button>
            )
          )}
        </Box>
      </Box>

      {/* Table */}
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 4,
          }}
        >
          <CircularProgress />
        </Box>
      ) : statusList?.data?.decryptedData?.total > 0 ? (
        <TableContainer
          sx={{
            overflow: "auto",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.action.hover,
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.action.disabled,
              borderRadius: "10px",
            },
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "background.paper",
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    borderBottom: "2px solid",
                    borderColor: "divider",
                  }}
                >
                  {t("Status")}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "background.paper",
                    minWidth: 120,
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    borderBottom: "2px solid",
                    borderColor: "divider",
                  }}
                >
                  {t("Background Colors")}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "background.paper",
                    minWidth: 120,
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    borderBottom: "2px solid",
                    borderColor: "divider",
                  }}
                >
                  {t("Status Icons")}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "background.paper",
                    minWidth: 150,
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    borderBottom: "2px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {t("Status Visibility")}
                    {isEditMode && (
                      <Tooltip
                        title={t("Visibility cannot be changed that have associated issues.")}
                        arrow
                        placement="top"
                      >
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: 16,
                            color: "text.secondary",
                            cursor: "help",
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "background.paper",
                    minWidth: 200,
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    borderBottom: "2px solid",
                    borderColor: "divider",
                  }}
                >
                  {t("Status Description")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(statusList?.data?.decryptedData?.items) &&
                statusList?.data?.decryptedData?.items?.length > 0 &&
                statusList?.data?.decryptedData?.items?.map((status, index) => (
                  <TableRow
                    key={status._id}
                    sx={{
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                      "&:nth-of-type(odd)": {
                        backgroundColor: "action.hover",
                      },
                      "&:nth-of-type(even)": {
                        backgroundColor: "background.paper",
                      },
                    }}
                  >
                    {/* Status Name */}
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        {status.status}
                      </Typography>
                    </TableCell>

                    {/* Background Color */}
                    <TableCell sx={{ py: 1.5 }}>
                      <Box
                        onClick={(e) => handleClick(e, status._id)}
                        sx={{
                          width: 40,
                          height: 30,
                          border: "2px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          cursor: isEditMode ? "pointer" : "default",
                          transition: "all 0.2s",
                          backgroundColor:
                            tempStatusColors[status._id] ||
                            status.color ||
                            "#000000",
                          opacity: isEditMode ? 1 : 0.7,
                          "&:hover": isEditMode
                            ? {
                                transform: "scale(1.05)",
                                boxShadow: 2,
                              }
                            : {},
                        }}
                      />
                    </TableCell>

                    {/* Status Icon */}
                    <TableCell sx={{ py: 1.5 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {tempStatusIcons[status._id] && (
                          <>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 32,
                                height: 32,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                opacity: isEditMode ? 1 : 0.7,
                              }}
                            >
                              <Icon
                                icon={tempStatusIcons[status._id]}
                                width="20"
                                height="20"
                                onError={() => {
                                  console.warn(
                                    `Failed to load icon: ${
                                      tempStatusIcons[status._id]
                                    }`
                                  );
                                }}
                              />
                            </Box>
                            {isEditMode && (
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveIcon(status._id)}
                                sx={{ p: 0.5 }}
                              >
                                <CloseIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            )}
                          </>
                        )}
                        {isEditMode && (
                          <IconButton
                            size="small"
                            onClick={() => handleIconClick(status._id)}
                            sx={{ p: 0.5 }}
                          >
                            <Icon
                              icon="material-symbols:image-search"
                              width="16"
                              height="16"
                            />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>

                    {/* Status Visibility */}
                    <TableCell sx={{ py: 1.5 }}>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={
                            tempStatusHideToggles[status._id]
                              ? "hidden"
                              : "visible"
                          }
                          onChange={(e) =>
                            handleHideToggleChange(
                              status._id,
                              e.target.value === "hidden"
                            )
                          }
                          IconComponent={KeyboardArrowDownIcon}
                          disabled={!isEditMode || status?.bugCount !== 0}
                          sx={{
                            "& .MuiSelect-select": {
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              py: 1,
                            },
                            opacity: isEditMode ? 1 : 0.7,
                          }}
                        >
                          <MenuItem value="visible">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <VisibilityIcon sx={{ fontSize: 16 }} />
                              {t("Visible")}
                            </Box>
                          </MenuItem>
                          <MenuItem
                            value="hidden"
                            disabled={status?.bugCount !== 0}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <VisibilityOffIcon sx={{ fontSize: 16 }} />
                              {t("Hidden")}
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                      {status?.bugCount !== 0 &&
                        tempStatusHideToggles[status._id] && (
                          <Typography
                            variant="caption"
                            color="error"
                            display="block"
                            sx={{ mt: 0.5 }}
                          >
                            {t("Cannot hide column with tasks")}
                          </Typography>
                        )}
                    </TableCell>

                    {/* Status Description */}
                    <TableCell sx={{ py: 1.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={t("Enter description...")}
                        value={tempStatusDescriptions[status._id] || ""}
                        onChange={(e) =>
                          handleDescriptionChange(status._id, e.target.value)
                        }
                        variant="outlined"
                        disabled={!isEditMode}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            fontSize: "0.875rem",
                            opacity: isEditMode ? 1 : 0.7,
                            "& input": {
                              py: 1,
                            },
                          },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
          }}
        >
          <Typography variant="body1">{t("No Status Found")}</Typography>
        </Box>
      )}
    </Paper>
  );

  // Due Date Colors Tab Content - Made more compact
  const dueDateColorsTab = (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        p: 3,
        maxHeight: "600px", // Reduced height
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Card Due Date Colors")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("Customize the colors of cards based on due date status")}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2, // Reduced gap
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: theme.palette.action.hover,
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.action.disabled,
            borderRadius: "10px",
          },
        }}
      >
        <Box>
          <ListItem
            sx={{
              py: 1,
              px: 1,
              "&:hover": {
                backgroundColor: "action.hover",
              },
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
            dense
          >
            <ListItemText
              primary={
                <Typography
                  variant="body1"
                  sx={{ color: "text.primary", fontWeight: 600 }}
                >
                  {t("Expired Card Color")}
                </Typography>
              }
              secondary={
                <Typography variant="body2" color="text.secondary">
                  {t("Color for cards with past due dates")}
                </Typography>
              }
            />
            <Box
              onClick={handleExpiredColorClick}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                padding: 1,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  boxShadow: 2,
                },
              }}
            >
              <Box
                sx={{
                  width: "40px",
                  height: "20px",
                  backgroundColor: expiredCardColor || "#ffebee",
                  borderRadius: 0.5,
                }}
              />
            </Box>
          </ListItem>

          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <Paper
              sx={{
                p: 2,
                width: "80%",
                bgcolor: expiredCardColor,
                border: "1px solid",
                borderColor: "divider",
                borderLeft: "4px solid #f44336",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t("Task example")}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                {t("Due")}:{" "}
                {new Date(Date.now() - 86400000).toLocaleDateString()}
              </Typography>
            </Paper>
          </Box>
        </Box>

        <Divider />

        <Box>
          <ListItem
            sx={{
              py: 1,
              px: 1,
              "&:hover": {
                backgroundColor: "action.hover",
              },
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
            dense
          >
            <ListItemText
              primary={
                <Typography
                  variant="body1"
                  sx={{ color: "text.primary", fontWeight: 600 }}
                >
                  {t("Near Expiry Card Color")}
                </Typography>
              }
              secondary={
                <Typography variant="body2" color="text.secondary">
                  {t("Color for cards due within 2 days")}
                </Typography>
              }
            />
            <Box
              onClick={handleNearExpiryColorClick}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                padding: 1,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  boxShadow: 2,
                },
              }}
            >
              <Box
                sx={{
                  width: "40px",
                  height: "20px",
                  backgroundColor: nearExpiryCardColor || "#fff8e1",
                  borderRadius: 0.5,
                }}
              />
            </Box>
          </ListItem>

          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <Paper
              sx={{
                p: 2,
                width: "80%",
                bgcolor: nearExpiryCardColor,
                border: "1px solid",
                borderColor: "divider",
                borderLeft: "4px solid #ff9800",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t("Task example")}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                {t("Due")}:{" "}
                {new Date(Date.now() + 86400000).toLocaleDateString()}
              </Typography>
            </Paper>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            pt: 2,
            mt: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveAppearance}
            disabled={loadingProject || !DEFEKT_TRACKER.UPDATE(myPermissions)}
          >
            {loadingProject ? t("Saving...") : t("Save Card Colors")}
          </Button>
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        display: "flex",
        flexDirection: "column",
        height: containerHeight > 0 ? `${containerHeight}px` : "calc(100vh - 200px)",
        minHeight: "300px",
      }}
      ref={containerRef}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="appearance settings tabs"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "0.95rem",
                fontWeight: 500,
                minWidth: 140,
              },
            }}
          >
            <Tab label={t("Appearance")} sx={{ gap: 1 }} />
            <Tab label={t("Due Date Colors")} sx={{ gap: 1 }} />
          </Tabs>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          <TabPanel value={activeTab} index={0}>
            {combinedAppearanceTab}
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            {dueDateColorsTab}
          </TabPanel>
        </Box>
      </Box>

      {/* Icon Selection Dialog */}
      <Dialog
        open={iconDialogOpen}
        onClose={() => isEditMode && setIconDialogOpen(false)} // Only allow closing if in edit mode
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "85vh",
            maxHeight: "900px",
            bgcolor: "background.paper",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">{t("Select Status Icon")}</Typography>
            <IconButton
              onClick={() => setIconDialogOpen(false)}
              sx={{ p: 1 }}
              disabled={!isEditMode}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            pb: 1,
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder={t(
                "Search icons... (e.g., check, bug, play, home, star)"
              )}
              value={searchIconTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{ mb: 2 }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Alert severity="info" sx={{ mb: 2 }}>
              {showingPopularIcons ? (
                <span>{t("Showing popular status icons")}</span>
              ) : (
                <span>
                  {t("Found")} {totalIconsCount.toLocaleString()} {t("icons")}
                  {selectedIconSet && ` ${t("in")} ${selectedIconSet}`}
                  {searchIconTerm && ` ${t("for")} "${searchIconTerm}"`}
                </span>
              )}
            </Alert>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))",
              alignContent: "start",
              gap: 1,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              bgcolor: "background.paper",
            }}
          >
            {isLoadingIcons ? (
              <Box
                sx={{
                  gridColumn: "1/-1",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "300px",
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t("Loading icons...")}
                  </Typography>
                </Box>
              </Box>
            ) : availableIcons.length > 0 ? (
              availableIcons.map((iconData) => (
                <Box
                  key={iconData.name}
                  onClick={() => handleIconSelect(iconData.name)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    cursor: isEditMode ? "pointer" : "default",
                    transition: "all 0.2s",
                    bgcolor: "background.paper",
                    opacity: isEditMode ? 1 : 0.6,
                    "&:hover": isEditMode
                      ? {
                          backgroundColor: "primary.light",
                          color: "white",
                          borderColor: "primary.main",
                          transform: "translateY(-3px)",
                          boxShadow: 2,
                        }
                      : {},
                  }}
                >
                  <Icon
                    icon={iconData.name}
                    width="32"
                    height="32"
                    style={{ marginBottom: "6px" }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      textAlign: "center",
                      lineHeight: 1.2,
                      wordBreak: "break-word",
                      textTransform: "capitalize",
                    }}
                  >
                    {iconData.label}
                  </Typography>
                </Box>
              ))
            ) : (
              <Box
                sx={{
                  gridColumn: "1/-1",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "300px",
                  textAlign: "center",
                }}
              >
                <Icon
                  icon="material-symbols:search-off"
                  width="64"
                  height="64"
                  style={{ opacity: 0.3, marginBottom: "16px" }}
                />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  {t("No icons found")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "Try a different search term, select a different collection, or use a custom icon name"
                  )}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t("Visit icones.co to browse all available icons")}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 2,
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("Page")} {iconPage} {t("of")} {totalPages}
              </Typography>
              <Pagination
                count={totalPages}
                page={iconPage}
                onChange={handlePageChange}
                size="small"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            bgcolor: "background.paper",
          }}
        >
          <Button
            onClick={() => setIconDialogOpen(false)}
            disabled={!isEditMode}
          >
            {t("Cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Color Popovers */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
          },
        }}
      >
        <ChromePicker
          color={
            tempStatusColors[selectedStatusId] ||
            statusList?.data?.decryptedData?.items?.find(
              (item) => item._id === selectedStatusId
            )?.color ||
            "#000000"
          }
          onChange={(color) => handleColorChange(selectedStatusId, color)}
          onChangeComplete={(color) =>
            handleColorChange(selectedStatusId, color)
          }
        />
      </Popover>

      <Popover
        open={openExpired}
        anchorEl={anchorElExpired}
        onClose={handleExpiredColorClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
          },
        }}
      >
        <ChromePicker
          color={expiredCardColor}
          onChange={(color) => setExpiredCardColor(color.hex)}
        />
      </Popover>

      <Popover
        open={openNearExpiry}
        anchorEl={anchorElNearExpiry}
        onClose={handleNearExpiryColorClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
          },
        }}
      >
        <ChromePicker
          color={nearExpiryCardColor}
          onChange={(color) => setNearExpiryCardColor(color.hex)}
        />
      </Popover>

      {(isLoading || loadingProject || isFetching || isSaving) && (
        <CustomLoader message={isSaving ? t("Saving changes...") : ""} />
      )}
    </Box>
  );
}
